import React from 'react';
import { Box, Paper, Typography, Collapse } from '@mui/material';
import {
  rankWith,
  LayoutProps,
  isLayout,
} from '@jsonforms/core';
import { withJsonFormsLayoutProps } from '@jsonforms/react';
import { JsonFormsDispatch } from '@jsonforms/react';
import { DENSITY_PRESETS, Density } from '../JsonFormsTheme';
import { cellStepsOf, formFieldCellSx, formFieldGridSx } from '../formFieldGrid';

/** A child the group places in its 12-column grid: a control, or a value object's block of them. */
const hasGridColumn = (element: any): boolean => Boolean(element.options?.gridColumn);

/**
 * Tester function to determine if this renderer should be used for Groups with gridColumn
 */
export const gridGroupRendererTester = rankWith(
  10, // High priority to override default Group renderer when gridColumn is present
  (uischema) => {
    if (!isLayout(uischema) || uischema.type !== 'Group') {
      return false;
    }
    // Match Groups with gridColumn children OR customFields placeholder
    if ((uischema as any).options?.customFields) {
      return true;
    }
    return uischema.elements?.some(hasGridColumn) || false;
  }
);

/** Shared section header styles */
const sectionHeaderSx = {
  fontSize: '0.8125rem',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  letterSpacing: '0.05em',
};

/**
 * GridGroup renderer component
 *
 * Renders a Group with CSS Grid layout for Controls that have gridColumn options.
 * A value object's block (`options.valueObject`) is such a Group nested in another: it spans its
 * gridColumn in the parent's grid and lays its own parts out in a grid of its own, with no label
 * and no padding of its own, so its parts line up with the controls beside it.
 * Supports density presets for compact/standard/comfortable spacing.
 * Responsive: the grid declares itself a form container, so each control's configured span steps
 * up towards full width as the *group* gets narrow (see fieldSpanSx) — independently of the
 * viewport, which says nothing about how much room the form was actually given.
 */
export const GridGroupRendererComponent = ({ uischema, schema, path, renderers, cells, label: translatedLabel }: LayoutProps) => {
  if (!isLayout(uischema) || uischema.type !== 'Group') {
    return null;
  }

  // Hide customFields placeholder when no UDF controls have been injected
  const isCustomFieldsPlaceholder = uischema.options?.customFields === true;
  if (isCustomFieldsPlaceholder && (!uischema.elements || uischema.elements.length === 0)) {
    return null;
  }

  // Use translated label from JSONForms i18n (falls back to uischema.label)
  const label = translatedLabel ?? (uischema as any).label ?? '';
  const collapsible = uischema.options?.collapsible === true;
  const [expanded, setExpanded] = React.useState(!collapsible);

  // Read density from options (propagated from parent layout)
  const density: Density = (uischema.options?.density as Density) || 'compact';
  const d = DENSITY_PRESETS[density];

  // Check if any child has gridColumn
  const hasGridColumns = uischema.elements?.some(hasGridColumn);

  // The configured span; how much of it survives at the current width is decided in CSS by
  // fieldSpanSx, against the grid below rather than the viewport.
  const getGridColumn = (element: any): number => {
    if (hasGridColumn(element)) {
      const match = element.options.gridColumn.match(/span\s+(\d+)/);
      if (match) return parseInt(match[1], 10);
    }
    return 12;
  };

  const content = (
    <>
      {hasGridColumns ? (
        <Box sx={formFieldGridSx(d.gridGap)}>
          {uischema.elements?.map((child, index) => {
            const spanValue = getGridColumn(child);
            const gridSize = Math.min(12, Math.max(1, spanValue));
            // A nested block lays out with the same density as the group it sits in
            const dispatched = child.type === 'Group'
              ? { ...child, options: { ...child.options, density, variant: uischema.options?.variant } }
              : child;
            const steps = cellStepsOf(child);

            return (
              // Configured spans are sized for a full-page form; the cell widens them a step at a
              // time as the group narrows, down to one control per row.
              <Box key={`${path}-${index}`} sx={formFieldCellSx(gridSize, steps.narrow, steps.parts)}>
                <JsonFormsDispatch
                  uischema={dispatched}
                  schema={schema}
                  path={path}
                  renderers={renderers}
                  cells={cells}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        // Fallback to vertical layout if no gridColumn
        uischema.elements?.map((child, index) => (
          <JsonFormsDispatch
            key={`${path}-${index}`}
            uischema={child}
            schema={schema}
            path={path}
            renderers={renderers}
            cells={cells}
          />
        ))
      )}
    </>
  );

  if (uischema.options?.valueObject === true) {
    return content;
  }

  if (collapsible) {
    return (
      <Paper sx={{ p: d.sectionPadding, mb: d.sectionMarginBottom }} elevation={expanded ? 0 : 1}>
        <Typography
          variant="subtitle2"
          onClick={() => setExpanded(!expanded)}
          sx={{
            cursor: 'pointer',
            fontWeight: 600,
            mb: expanded ? d.sectionHeaderMarginBottom : 0,
            userSelect: 'none',
            ...sectionHeaderSx,
          }}
        >
          {expanded ? '▼' : '▶'} {label}
        </Typography>
        <Collapse in={expanded}>
          {content}
        </Collapse>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: d.sectionPadding, mb: d.sectionMarginBottom }}>
      {label && (
        <Typography
          variant="subtitle2"
          gutterBottom
          fontWeight={600}
          sx={{ mb: d.sectionHeaderMarginBottom, ...sectionHeaderSx }}
        >
          {label}
        </Typography>
      )}
      {content}
    </Box>
  );
};

/**
 * GridGroup renderer entry for JSON Forms
 */
export const gridGroupRendererEntry = {
  tester: gridGroupRendererTester,
  renderer: withJsonFormsLayoutProps(GridGroupRendererComponent),
};
